import React from "react";
import {
  formatEuro,
  formatPricingBreakdownSummary,
  pricingPlanLabel,
} from "../../utils/deletionCasePricing";
import { useTranslation } from "../../../i18n";

/**
 * Full pricing breakdown for expanded admin/client case details.
 */
const DeletionCasePricingPanel = ({ pricing, items = [] }) => {
  const { t } = useTranslation();

  if (!pricing) return null;

  const itemsWithPrice = items.map((item) => ({
    ...item,
    unitPrice: item.unitPrice ?? 0,
    lineTotal: item.lineTotal ?? item.unitPrice ?? 0,
    isFree: item.isFree ?? false,
  }));

  return (
    <section
      className="rounded-[12px] border border-[#006666]/15 bg-[#f0fafa] p-4 md:p-5"
      aria-label={t("deletionCaseUi.pricingOverview")}
    >
      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[#64748b]">
        {t("deletionCaseUi.pricingOverview")}
      </h3>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-b border-[#cce8e8] pb-4">
        <div>
          <p className="text-[12px] text-[#64748b]">{t("deletionCaseUi.caseTotal")}</p>
          <p className="text-[28px] font-bold tabular-nums text-[#006666]">
            {formatEuro(pricing.caseTotal)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[12px] text-[#64748b]">{t("deletionCaseUi.package")}</p>
          <p className="text-[14px] font-semibold text-[#0f172a]">
            {pricingPlanLabel(pricing.billingPlan)}
          </p>
          {pricing.tierLabel ? (
            <p className="text-[12px] text-[#64748b]">{pricing.tierLabel}</p>
          ) : null}
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-[13px] sm:grid-cols-4">
        <div>
          <dt className="text-[11px] font-semibold uppercase text-[#94a3b8]">
            {t("deletionCaseUi.perReview")}
          </dt>
          <dd className="mt-0.5 font-semibold tabular-nums text-[#0f172a]">
            {pricing.billingPlan === "subscriber" && pricing.freeReviewsApplied > 0
              ? `${formatEuro(0)} – ${formatEuro(pricing.pricePerReview)}`
              : formatEuro(pricing.pricePerReview)}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase text-[#94a3b8]">
            {t("deletionCaseUi.package")}
          </dt>
          <dd className="mt-0.5 text-[#475569]">
            {formatPricingBreakdownSummary(pricing)}
          </dd>
        </div>
        {pricing.billingPlan === "subscriber" ? (
          <div>
            <dt className="text-[11px] font-semibold uppercase text-[#94a3b8]">
              {t("deletionCaseUi.free")}
            </dt>
            <dd className="mt-0.5 font-semibold text-[#0f172a]">
              {t("deletionCaseUi.reviewsInCase", {
                count: pricing.freeReviewsApplied,
              })}
            </dd>
          </div>
        ) : null}
        {pricing.billingPlan === "subscriber" ? (
          <div>
            <dt className="text-[11px] font-semibold uppercase text-[#94a3b8]">
              {t("deletionCaseUi.perReview")}
            </dt>
            <dd className="mt-0.5 font-semibold text-[#0f172a]">
              {pricing.paidReviewCount} × {formatEuro(pricing.pricePerReview)}
            </dd>
          </div>
        ) : null}
      </dl>

      {pricing.isEstimated ? (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          {t("deletionCaseUi.estimateHint")}
        </p>
      ) : null}

      {itemsWithPrice.length > 0 ? (
        <div className="mt-5">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-[#94a3b8]">
            {t("deletionCaseUi.perReview")}
          </p>
          <div className="overflow-x-auto rounded-lg border border-[#e8ecf0] bg-white">
            <table className="w-full min-w-[400px] text-left text-[13px]">
              <thead className="border-b border-[#e8ecf0] bg-[#fafbfc]">
                <tr>
                  <th className="px-3 py-2 font-semibold text-[#64748b]">
                    {t("deletionCaseUi.reviewsForDeletion")}
                  </th>
                  <th className="px-3 py-2 font-semibold text-[#64748b]">
                    {t("deletionCaseUi.perReview")}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-[#64748b]">
                    {t("deletionCaseUi.caseTotal")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {itemsWithPrice.map((item, idx) => (
                  <tr
                    key={item.id ?? item.reviewId ?? idx}
                    className="border-b border-[#f1f5f9] last:border-0"
                  >
                    <td className="px-3 py-2.5">
                      <span className="font-medium text-[#0f172a]">
                        {item.author || "—"}
                      </span>
                      <span className="ml-2 text-[#94a3b8]">
                        #{item.reviewId}
                      </span>
                      {item.isFree ? (
                        <span className="ml-2 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                          {t("deletionCaseUi.included")}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-[#475569]">
                      {item.isFree ? (
                        <span className="text-emerald-700">€0</span>
                      ) : (
                        formatEuro(item.unitPrice)
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-[#0f172a]">
                      {formatEuro(item.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-[#e8ecf0] bg-[#fafbfc]">
                <tr>
                  <td
                    colSpan={2}
                    className="px-3 py-2.5 text-right text-[12px] font-semibold text-[#64748b]"
                  >
                    {t("deletionCaseUi.caseSum")}
                  </td>
                  <td className="px-3 py-2.5 text-right text-[14px] font-bold tabular-nums text-[#006666]">
                    {formatEuro(pricing.caseTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default DeletionCasePricingPanel;
